import React, { useCallback, useEffect, useMemo, useState } from "react"

import { useCopy } from "components/hooks/use-copy"
import { useNoScroll } from "components/hooks/use-no-scroll"
import { useTailwindBreakpoints } from "components/hooks/use-tailwind-breakpoints"

import { Box } from "components/shared/box"
import { IosAdd } from "components/shared/icons/ios-add"
import { SafariIcon } from "components/shared/icons/safari-icon"
import { ShareIos } from "components/shared/icons/share-ios"
import { ThreeDotsIcon } from "components/shared/icons/three-dots-icon"
import { Button } from "components/shared/landing/button"
import { Text } from "components/shared/text"

import { detectBrowser, detectInApp, detectMobile, detectPlatform, detectWebview } from "utils/browser/detect-platform"
import { isPWA } from "utils/browser/is-pwa"

type BeforeInstallEvent = Event & { prompt: () => void; userChoice: never }

// const DYNAMIC_LINK = "https://cryptobots.page.link/29hQ"

export function PwaInstallModal({ type }: { type: "login" | "register" }) {
  const { isLandscape } = useTailwindBreakpoints()
  const { copy, copied } = useCopy()
  const { setNoScroll } = useNoScroll()
  // const router = useRouter()

  const [deferredPrompt, setDeferredPrompt] = useState<BeforeInstallEvent | undefined>()

  const installPWA = useCallback(async () => {
    if (deferredPrompt) {
      deferredPrompt.prompt()
      const { outcome } = await deferredPrompt.userChoice
      if (outcome === "accepted") {
        setDeferredPrompt(undefined)
      }
    }
  }, [deferredPrompt])

  const isMobile = useMemo(() => {
    return detectMobile()
  }, [])

  const isIOS = useMemo(() => {
    return detectPlatform() === "ios"
  }, [])

  const isSafari = useMemo(() => {
    return detectBrowser() === "safari"
  }, [])

  const isChrome = useMemo(() => {
    return detectBrowser() === "chrome"
  }, [])

  const isWebview = useMemo(() => {
    return detectWebview()
  }, [])

  const isInApp = useMemo(() => {
    return detectInApp()
  }, [])

  const isInPwa = useMemo(() => {
    return isPWA()
  }, [])

  const desktopOrWebView = !isMobile || isWebview || isInApp
  const mobileDevice = !desktopOrWebView

  // Оставить, для того момента, когда будем дальше тестить работу модалки
  // const log = JSON.stringify({
  //   isIOS,
  //   isSafari,
  //   isChrome,
  //   isWebview,
  //   isInApp,
  //   isInPwa,
  //   desktopOrWebView,
  //   mobileDevice,
  //   userAgent: typeof window !== "undefined" && window.navigator.userAgent,
  // }).replaceAll(",", ",\n")

  useEffect(() => {
    if (mobileDevice && !isInPwa) {
      window.addEventListener("beforeinstallprompt", e => {
        setDeferredPrompt(e as BeforeInstallEvent)
      })
      setNoScroll(true)

      return () => {
        setNoScroll(false)
      }
    }
  })

  if (isInPwa || !isMobile) {
    return null
  }

  // TODO пока убрал кнопку, Радмир попросил сделать как на iOS
  // if ((isWebview || isInApp) && !isIOS) {
  //   return (
  //     <Box center className="fixed inset-0 backdrop-blur-3xl z-50 p-4">
  //       <Box center className="m-auto text-black/90 bg-white rounded-box p-4 gap-3">
  //         <Box className="font-bold text-[20px] text-center">
  //           Open Chrome browser {type === "login" && "to complete log in"}{" "}
  //           {type === "register" && "to complete your registration"}
  //         </Box>
  //         <Box className="mt-4">
  //           <Button type="primary" onClick={() => router.push(DYNAMIC_LINK)}>
  //             Open
  //           </Button>
  //         </Box>
  //       </Box>
  //     </Box>
  //   )
  // }

  if ((isWebview || isInApp) && !isIOS) {
    return (
      <Box center className="fixed inset-0 z-50 p-4 backdrop-blur-3xl">
        <Box center className="m-auto gap-3 rounded-box bg-white p-4 text-black/90">
          <Box className="text-center text-[20px] font-bold">
            Open Chrome browser {type === "login" && "to complete log in"}{" "}
            {type === "register" && "to complete your registration"}
          </Box>
          <span className="mt-3 text-center text-[18px]">
            Tap on
            <ThreeDotsIcon w={24} h={24} className="ml-2 inline-flex" />
            <br />
            and then select “Open in Browser”
          </span>
        </Box>
      </Box>
    )
  }

  if ((isWebview || isInApp) && isIOS) {
    return (
      <Box center className="fixed inset-0 z-50 p-4 backdrop-blur-3xl">
        <Box center className="m-auto gap-3 rounded-box bg-white p-4 text-black/90">
          <Box className="text-center text-[20px] font-bold">
            Open Safari browser {type === "login" && "to complete log in"}{" "}
            {type === "register" && "to complete your registration"}
          </Box>
          <span className="mt-3 text-center text-[18px]">
            Tap on
            <ThreeDotsIcon w={24} h={24} className="ml-2 inline-flex" />
            <br />
            and then select “Open in Browser”
            <br />
            <br />
            <strong>OR</strong>
            <br />
            <br />
            <Box center row>
              Click <SafariIcon w={24} h={24} className="ml-2 inline-flex" fill="#0974f6" />
            </Box>
          </span>
        </Box>
      </Box>
    )
  }

  // if (!isMobile) {
  //   return (
  //     <Box center className="fixed inset-0 backdrop-blur-3xl z-50 p-4">
  //       <Box center className="m-auto text-black/90 bg-white rounded-box p-4 gap-3">
  //         <Box className="font-bold text-[20px]">Install Cryptobots</Box>
  //         <Box className="font-bold text-[20px]">
  //           To proceed please go to the browser (Safari on iOS, Chrome on Android)
  //         </Box>
  //         {onClose && (
  //           <Box className="mt-4">
  //             <Button type="primary" onClick={onClose}>
  //               Ok
  //             </Button>
  //           </Box>
  //         )}
  //       </Box>
  //     </Box>
  //   )
  // }

  if (isSafari) {
    return (
      <Box className="fixed inset-0 z-50 p-4 text-black backdrop-blur-3xl">
        {isLandscape && (
          <Box className="ml-auto mr-[134px] h-[20px]">
            <Box className="mx-auto border border-[0_20px_20px_20px] border-[transparent_transparent_#ffffff_transparent]" />
          </Box>
        )}
        <Box center className={`${isLandscape ? "mb-auto ml-auto" : "mt-auto"} rounded-box bg-white p-4`}>
          <Box className="text-[20px] font-bold">Add Cryptobots to Home Screen</Box>
          <span className="mt-3 text-[18px]">
            Tap on
            <ShareIos fill="#74ADE9" w={24} h={24} className="inline-flex" />
            and then select “Add to Home Screen <IosAdd fill="#74ADE9" w={18} h={18} className="mx-1 inline-flex" />”
          </span>
        </Box>
        {!isLandscape && (
          <Box className="h-[20px]">
            <Box className="mx-auto border border-[#ffffff_transparent_transparent_transparent] border-[20px_20px_0_20px]" />
          </Box>
        )}
      </Box>
    )
  }

  if (isChrome) {
    return (
      <Box center className="fixed inset-0 z-50 p-4 backdrop-blur-3xl">
        <Box center className="m-auto rounded-box bg-white p-4">
          <Box className="text-[20px] font-bold text-black">Please install Cryptobots</Box>
          <span className="mt-3 text-[18px]">
            Tap Install and follow the on-screen instructions.
            <br />
            Then launch Cryptobots on your home screen.
          </span>
          <Box className="mt-4">
            <Button type="primary" onClick={installPWA}>
              Install
            </Button>
          </Box>
        </Box>
      </Box>
    )
  }

  return (
    <Box center className="fixed inset-0 z-50 p-4 text-black backdrop-blur-3xl">
      <Box center className="m-auto rounded-box bg-white p-4">
        <Box className="text-center text-[18px] font-bold">
          Please open Cryptobots via {isIOS ? "Safari" : "Chrome"}!
        </Box>
        <span className="mt-3 select-text">
          To install the game open{" "}
          <span
            onClick={() => {
              copy("https://cryptobots.me")
            }}
            className="relative text-primary"
          >
            cryptobots.me{" "}
            {copied && (
              <Box className="absolute top-10">
                <Text size="tiny" className="rounded bg-white p-1 text-black opacity-60">
                  Address copied!
                </Text>
              </Box>
            )}
          </span>{" "}
          via {isIOS ? "Safari" : "Chrome"} browser
        </span>
      </Box>
    </Box>
  )
}
